import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import OpenAI from 'openai';

const PromptInterface = ({ onGenerate, selectedTemplate, selectedDataSources, isGenerating, setIsGenerating }) => {
  const [prompt, setPrompt] = useState('');
  const [generationType, setGenerationType] = useState('executive-summary');

  const quickPrompts = [
    {
      id: 1,
      label: 'Generate Executive Summary',
      prompt: 'Create a comprehensive executive summary of our safety performance for this quarter, highlighting key metrics, trends, and achievements.',
      type: 'executive-summary',
    },
    {
      id: 2,
      label: 'Analyze Incident Trends',
      prompt: 'Analyze incident and near-miss trends over the past 6 months, identify patterns, root causes, and provide actionable insights.',
      type: 'trend-analysis',
    },
    {
      id: 3,
      label: 'Compliance Status Report',
      prompt: 'Generate a detailed compliance status report covering all regulatory requirements, audit findings, and corrective actions.',
      type: 'compliance',
    },
    {
      id: 4,
      label: 'Safety Recommendations',
      prompt: 'Based on recent safety data and observations, provide prioritized recommendations for improving our safety program.',
      type: 'recommendations',
    },
  ];

  const generationTypes = [
    { value: 'executive-summary', label: 'Executive Summary', icon: 'FileText' },
    { value: 'trend-analysis', label: 'Trend Analysis', icon: 'TrendingUp' },
    { value: 'compliance', label: 'Compliance Statement', icon: 'Shield' },
    { value: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' },
    { value: 'custom', label: 'Custom Generation', icon: 'Sparkles' },
  ];

  const handleQuickPrompt = (quickPrompt) => {
    setPrompt(quickPrompt?.prompt);
    setGenerationType(quickPrompt?.type);
  };

  const handleGenerate = async () => {
    if (!prompt?.trim()) return;

    setIsGenerating(true);

    try {
      const openai = new OpenAI({
        apiKey: import.meta.env.VITE_OPENAI_API_KEY,
        dangerouslyAllowBrowser: true,
      });

      const systemPrompt = `You are an expert safety report writer specializing in industrial safety management. 
      Generate professional, detailed, and actionable safety report content based on the user's request. 
      Focus on clarity, regulatory compliance, and practical recommendations.
      
      Generation Type: ${generationType}
      Selected Template: ${selectedTemplate?.name || 'None'}
      Available Data Sources: ${selectedDataSources?.map(s => s?.name)?.join(', ') || 'None'}
      
      Format the output as a well-structured narrative suitable for inclusion in a safety report.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt },
        ],
        reasoning_effort: 'medium',
        verbosity: 'high',
        max_completion_tokens: 2000,
      });

      const generatedContent = response?.choices?.[0]?.message?.content || '';
      onGenerate(generatedContent);
    } catch (error) {
      console.error('Error generating content:', error);
      onGenerate('Error generating content. Please check your API key and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Generation Type
        </label>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {generationTypes?.map((type) => (
            <button
              key={type?.value}
              onClick={() => setGenerationType(type?.value)}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                generationType === type?.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <Icon name={type?.icon} size={24} color={generationType === type?.value ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
              <span className="text-xs font-medium text-center">{type?.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Describe What You Want to Generate
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e?.target?.value)}
          placeholder="Example: Generate an executive summary highlighting our Q4 safety performance, including incident rates, training completion, and key achievements..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-foreground"
          rows={6}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-muted-foreground">
            {prompt?.length}/2000 characters
          </p>
          <button
            onClick={handleGenerate}
            disabled={!prompt?.trim() || isGenerating}
            className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="Sparkles" size={18} />
            {isGenerating ? 'Generating...' : 'Generate Content'}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Quick Prompts
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {quickPrompts?.map((quickPrompt) => (
            <button
              key={quickPrompt?.id}
              onClick={() => handleQuickPrompt(quickPrompt)}
              className="flex items-start gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Zap" size={16} color="var(--color-primary)" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">{quickPrompt?.label}</h4>
                <p className="text-xs text-muted-foreground line-clamp-2">{quickPrompt?.prompt}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PromptInterface;